import { CurrentImageProps } from '@fabric-web/3d-viewer';
import queryString from 'query-string';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useCallback, useEffect, useRef } from 'react';
import * as globalState from '@/state';
import * as analysisState from '@/components/Analysis/state';
import {
  blisterToAdd,
  selectedPointOfInterestId,
} from '@/components/Analysis/modules/pointOfInterest/state';
import { mapLocationToCurrentImage } from './Viewer/hooks/useImageFromLocationId';
import { useGetAssemblyByTagNameLazyQuery } from '@/__generated__/graphql';
import { useGetStructureId } from '@/hooks/useGetStructureId';
import { AnalysisDrawerLeft, LastDeeplinkParameters } from '@/types';
import { toggleValue } from '@/utils';
import { useCameraTargetDeeplink } from '@/hooks/deeplink/useCameraTargetDeeplink';
import { useAssemblyDeeplink } from '@/hooks/deeplink/useAssemblyDeeplink';
import { usePoiDeeplink } from '@/hooks/deeplink/usePoiDeeplink';
import { useDefectsDeeplink } from '@/hooks/deeplink/useDefectsDeeplink';
import { useFiltersDeeplink } from '@/hooks/deeplink/useFiltersDeeplink';
import { useCameraSettingsDeeplink } from '@/hooks/deeplink/useCameraSettingsDeeplink';
import { usePointCloudDeeplink } from '@/hooks/deeplink/usePointCloudDeeplink';
import { parseFloatParameter, updateCameraTargetUrlParameters } from '@/hooks/deeplink/utils';

export { updateCameraTargetUrlParameters, parseFloatParameter };

export const useAnalysisDeeplink = () => {
  const [lastDeeplinkUrlParameters, setLastDeeplinkUrlParameters] = useAtom(
    analysisState.lastDeeplinkUrlParameters
  );
  const inspectionMetadata = useAtomValue(analysisState.inspectionMetadata);
  const structureLocations = useAtomValue(analysisState.structureLocations);
  const [statesLoaded, setStatesLoaded] = useAtom(globalState.statesLoaded);
  const setSelectedAssemblyId = useSetAtom(analysisState.selectedAssemblyId);
  const setBlisterToAdd = useSetAtom(blisterToAdd);
  const setAbyssViewerState = useSetAtom(globalState.abyssViewerState);
  const [drawerLeft, setDrawerLeft] = useAtom(analysisState.drawerLeft);
  const setSelectedSpherical = useSetAtom(analysisState.selectedSpherical);
  const screenshotMode = useAtomValue(analysisState.screenshotMode);

  const [getAssemblyId] = useGetAssemblyByTagNameLazyQuery();
  const structureId = useGetStructureId();

  const isComplete = useRef(false);
  const parametersRef = useRef<LastDeeplinkParameters | null>(null);

  const drawerLeftRef = useRef(drawerLeft);
  drawerLeftRef.current = drawerLeft;

  const shouldUpdateUrl = !screenshotMode && statesLoaded;

  const onAssemblyParsed = useCallback(
    (tagName: string) => {
      if (structureId) {
        getAssemblyId({ variables: { tagName, structureId } }).then(({ data }) => {
          const assemblyId = data?.assemblyByTagName?.id;
          if (assemblyId) setSelectedAssemblyId(assemblyId);
        });
      }
    },
    [structureId, getAssemblyId, setSelectedAssemblyId]
  );

  const onPoiParsed = useCallback(() => {
    setBlisterToAdd({ state: 'Reviewing' });
  }, [setBlisterToAdd]);

  const onShowOnlySelectedPoiParsed = useCallback(() => {
    setDrawerLeft(toggleValue<AnalysisDrawerLeft>(drawerLeftRef.current, 'visibility'));
  }, [setDrawerLeft]);

  const onSphericalSelected = useCallback(
    (image: CurrentImageProps) => {
      setAbyssViewerState({ mode: 'Spherical' });
      setSelectedSpherical(image);
      if (parametersRef.current) {
        parametersRef.current['selectedSpherical'] = image;
        if (image.lookAt) {
          parametersRef.current['lookAt'] = [image.lookAt.x, image.lookAt.y, image.lookAt.z];
        }
      }
    },
    [setAbyssViewerState, setSelectedSpherical]
  );

  const onCameraTargetSet = useCallback((target: { lookAt: any }) => {
    if (parametersRef.current) {
      parametersRef.current['lookAt'] = target.lookAt;
    }
  }, []);

  const { applyFromParsed: applyDefects } = useDefectsDeeplink({
    shouldUpdateUrl,
    selectedStructureDefectsLegendsAtom: analysisState.selectedStructureDefectsLegends as any,
  });

  const { applyFromParsed: applyAssembly } = useAssemblyDeeplink({
    shouldUpdateUrl,
    selectedAssemblyNameAtom: analysisState.selectedAssemblyName,
    onAssemblyParsed,
  });

  const { applyFromParsed: applyPoi } = usePoiDeeplink({
    shouldUpdateUrl,
    selectedPointOfInterestIdAtom: selectedPointOfInterestId,
    showOnlySelectedPoiAtom: analysisState.showOnlySelectedPoi,
    showPointOfInterestPointerAtom: analysisState.showPointOfInterestPointer,
    onPoiParsed,
    onShowOnlySelectedPoiParsed,
  });

  const { applyFromParsed: applyCameraTarget } = useCameraTargetDeeplink({
    shouldUpdateUrl,
    cameraTargetAtom: analysisState.cameraTarget,
    selectedSphericalAtom: analysisState.viewerNextCurrentSpherical,
    locations: structureLocations,
    mapLocationToCurrentImage,
    onSphericalSelected,
    onCameraTargetSet,
  });

  const { applyFromParsed: applyCameraSettings } = useCameraSettingsDeeplink({
    shouldUpdateUrl,
    cameraTypeAtom: globalState.cameraType,
    cameraDirectionAtom: globalState.cameraDirection,
  });

  const { applyFromParsed: applyPointCloud } = usePointCloudDeeplink({
    shouldUpdateUrl,
    pointBudgetAtom: globalState.pointBudget,
    enableAdaptivePointBudgetAtom: globalState.enableAdaptivePointBudget,
  });

  const { applyFromParsed: applyFilters } = useFiltersDeeplink({
    shouldUpdateUrl,
    selectedTemplateFiltersAtom: analysisState.selectedTemplateFiltersState,
  });

  useEffect(() => {
    if (!statesLoaded && lastDeeplinkUrlParameters) {
      setStatesLoaded(true);
    }
  }, [statesLoaded, setStatesLoaded, lastDeeplinkUrlParameters]);

  useEffect(() => {
    if (isComplete.current) return;
    const query = window.location.search;
    if (query === lastDeeplinkUrlParameters?.query) return;

    setStatesLoaded(false);

    if (inspectionMetadata && structureLocations !== undefined && structureId) {
      const parameters: LastDeeplinkParameters = { query, time: performance.now() };
      parametersRef.current = parameters;

      const parsed = queryString.parse(query, { arrayFormat: 'comma' });

      applyDefects(parsed);
      applyAssembly(parsed);
      applyPoi(parsed);
      applyCameraTarget(parsed);
      applyCameraSettings(parsed);
      applyPointCloud(parsed);
      applyFilters(parsed);

      setLastDeeplinkUrlParameters(parametersRef.current);

      isComplete.current = true;
      setStatesLoaded(true);
    }
  }, [
    structureId,
    inspectionMetadata,
    structureLocations,
    statesLoaded,
    setStatesLoaded,
    lastDeeplinkUrlParameters,
    setLastDeeplinkUrlParameters,
    applyDefects,
    applyAssembly,
    applyPoi,
    applyCameraTarget,
    applyCameraSettings,
    applyPointCloud,
    applyFilters,
  ]);
};
